import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environments/environment';
import { CategoriesMap, Category, ConnectionSteps, LastConfigurationData, DataFromMultivende, PriceList, Warehouse, PictureSet, ResponsePolling, CategoriesMapForQuery } from '@models/multivende.model';
import { GraphQlQuery } from '@models/query.model';
import { BehaviorSubject, map, of, switchMap, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class MultivendeService {
  private http = inject(HttpClient);

  polling = new BehaviorSubject<any>({});
  pollingProducts$ = this.polling.asObservable();

  checkConnectionSteps(seller: string){
    const query: GraphQlQuery = {
      query: `query CheckMultivendeConnection($seller: ObjectID!) {
        CheckMultivendeConnection(seller: $seller) {
          exists
          stepOne
          stepTwo
          stepThree
          stepFour
        }
      }`,
      variables: {
        seller: seller
      }
    };
    return this.http.post<{errors?: Error[], data: { CheckMultivendeConnection: ConnectionSteps }}>(environment.multivendeApiUri, query)
      .pipe(map(response => response.data));
  }

  verifyConnectionSteps(seller: string){
    const query: GraphQlQuery = {
      query: `query VerifyMultivendeConnection($seller: ObjectID!) {
        VerifyMultivendeConnection(seller: $seller) {
          exists
          stepOne
          stepTwo
          stepThree
          stepFour
        }
      }`,
      variables: {
        seller: seller
      }
    };
    return this.http.post<{errors?: Error[], data: { VerifyMultivendeConnection: ConnectionSteps }}>(environment.multivendeApiUri, query)
      .pipe(map(response => response.data));
  }

  createConnection(seller: string, token: string){
    const query: GraphQlQuery = {
      query: `mutation CreateConnection($seller: ObjectID! $token: NonEmptyString!) {
        ConnectToMultivende(seller: $seller token: $token) {
            success
        }
      }`,
      variables: {
        seller: seller,
        token: token,
      }
    };
    return this.http.post<{errors?: Error[], data: { ConnectToMultivende: { success: boolean } }}>(environment.multivendeApiUri, query)
      .pipe(
        switchMap((response) => response.data.ConnectToMultivende ? of(response.data.ConnectToMultivende) : throwError(() => new Error(response.errors?.[0].message)))
      );
  }

  reconnectConnection(seller: string, token: string) {
    const query: GraphQlQuery = {
      query: `mutation ReconnectToMultivende($seller: ObjectID! $token: NonEmptyString!) {
        ReconnectToMultivende(seller: $seller token: $token) {
            success
            message
        }
      }`,
      variables: {
        seller: seller,
        token: token,
      }
    };
    return this.http.post<{ errors?: Error[], data: { ReconnectToMultivende: { message: string, success: boolean } } }>(environment.multivendeApiUri, query)
      .pipe(
        switchMap((response) => response.data.ReconnectToMultivende ? of(response.data.ReconnectToMultivende) : throwError(() => new Error(response.errors?.[0].message)))
      );
  }

  getWarehouses(seller: string){
    const query: GraphQlQuery = {
      query: `query GetStoresAndWarehouses($seller: ObjectID!) {
        GetStoresAndWarehouses(seller: $seller ) {
            id
            name
            type
        }
      }`,
      variables: {
        seller: seller
      }
    };
    return this.http.post<{errors?: Error[], data: { GetStoresAndWarehouses: Warehouse[]}}>(environment.multivendeApiUri, query)
      .pipe(map(response => response.data.GetStoresAndWarehouses));
  }

  getCategories(seller: string){
    const query: GraphQlQuery = {
      query: `query GetCategoriesFromMultivende($seller: ObjectID!) {
        GetCategoriesFromMultivende(seller: $seller) {
            _id
            name
        }
      }`,
      variables: {
        seller: seller
      }
    };
    return this.http.post<{errors?: Error[], data: { GetCategoriesFromMultivende: Category[]}}>(environment.multivendeApiUri, query)
      .pipe(map(respose => respose.data.GetCategoriesFromMultivende));
  }

  getPriceList(seller: string){
    const query: GraphQlQuery = {
      query: `query GetPriceListFromMultivende($seller: ObjectID!) {
        GetPriceListFromMultivende(seller: $seller ) {
            id
            name
            currency
            symbol
        }
      }`,
      variables: {
        seller: seller
      }
    };
    return this.http.post<{errors?: Error[], data: { GetPriceListFromMultivende: PriceList[]}}>(environment.multivendeApiUri, query)
      .pipe(map(response => response.data.GetPriceListFromMultivende));
  }

  getAllDataFromMultivende(seller: string){
    const query: GraphQlQuery = {
      query: `query GetAllDataFromMultivende($seller: ObjectID!) {
        GetStoresAndWarehouses(seller: $seller ) {
            id
            name
            type
            address
        }
        GetCategoriesFromMultivende(seller: $seller) {
            _id
            name
        }
        GetPriceListFromMultivende(seller: $seller ) {
            id
            name
            currency
            symbol
        }
        GetProductsPictureSet(seller: $seller ) {
          id
          name
          description
      }
    }`,
      variables: {
        seller: seller
      }
    };
    return this.http.post<{errors?: Error[], data: DataFromMultivende}>(environment.multivendeApiUri, query)
      .pipe(map(response => response.data));
  }

  setDefaultWarehouse(seller: string, warehouse: Warehouse){
    const query: GraphQlQuery = {
      query: `mutation SetDefaultStoreOrWarehouse($seller: ObjectID! $warehouse: WarehouseInput!) {
        SetDefaultStoreOrWarehouse(seller: $seller warehouse: $warehouse) {
            success
            message
        }
      }`,
      variables: {
        seller: seller,
        warehouse: {
          id: warehouse.id,
          name: warehouse.name,
          address: warehouse.address
        }
      }
    };
    return this.http.post<{errors?: Error[], data: { SetDefaultStoreOrWarehouse: { message: string, success: boolean }}}>(environment.multivendeApiUri, query)
      .pipe(
        switchMap((response) => response.data.SetDefaultStoreOrWarehouse ? of(response.data.SetDefaultStoreOrWarehouse) : throwError(() => new Error(response.errors?.[0].message)))
      );
  }

  setCategoriesMap(seller: string, map: CategoriesMapForQuery[]){
    const query: GraphQlQuery = {
      query: `mutation SetCategoriesMap($seller: ObjectID! $map: [CategoryMapInput!]! ) {
        SetCategoriesMap(seller: $seller map: $map) {
            success
            message
        }
    }`,
      variables: {
        seller: seller,
        map: map
      }
    };
    return this.http.post<{errors?: Error[], data: { SetCategoriesMap: { message: string, success: boolean }}}>(environment.multivendeApiUri, query)
      .pipe(
        switchMap((response) => response.data.SetCategoriesMap ? of(response.data.SetCategoriesMap) : throwError(() => new Error(response.errors?.[0].message)))
      );
  }

  setDefaultPriceList(seller: string, pricelist: PriceList){
    const query: GraphQlQuery = {
      query: `mutation SetDefaultPriceList($seller: ObjectID! $priceList: PriceListInput!) {
        SetDefaultPriceList(seller: $seller priceList: $priceList) {
            success
            message
        }
      }`,
      variables: {
        seller: seller,
        priceList: pricelist
      }
    };
    return this.http.post<{errors?: Error[], data: { SetDefaultPriceList: { message: string, success: boolean }}}>(environment.multivendeApiUri, query)
      .pipe(
        switchMap((response) => response.data.SetDefaultPriceList ? of(response.data.SetDefaultPriceList) : throwError(() => new Error(response.errors?.[0].message)))
      );
  }

  setProductPictureSet(seller: string, pictureSet: PictureSet){
    const query: GraphQlQuery = {
      query: `mutation SetProductsPictureSet($seller: ObjectID! $productsPictureSet: ProductsPictureSetInput!) {
        SetProductsPictureSet(seller: $seller productsPictureSet: $productsPictureSet) {
            success
            message
        }
    }`,
      variables: {
        seller: seller,
        productsPictureSet: {
          id: pictureSet.id,
          name: pictureSet.name
        }
      }
    };
    return this.http.post<{errors?: Error[], data: { SetProductsPictureSet: { message: string, success: boolean }}}>(environment.multivendeApiUri, query)
      .pipe(
        switchMap((response) => response.data.SetProductsPictureSet ? of(response.data.SetProductsPictureSet) : throwError(() => new Error(response.errors?.[0].message)))
      );
  }

  getCurrentDataConfiguration(seller: string){
    const query: GraphQlQuery = {
      query: `query MultivendeConnectionData($seller: ObjectID!) {
        MultivendeConnectionData(seller: $seller) {
            seller
            warehouse {
                id
                name
            }
            categories_map {
                category {
                    _id
                    name
                    profit
                }
                multivende_category {
                    _id
                    name
                }
            }
            price_list {
                id
                name
                currency
                symbol
            }
            products_picture_set{
              id
              name
            }
        }
    }`,
      variables: {
        seller: seller
      }
    };
    return this.http.post<{errors?: Error[], data: { MultivendeConnectionData: LastConfigurationData}}>(environment.multivendeApiUri, query)
      .pipe(map(response => response.data.MultivendeConnectionData));
  }

  validatePolling(seller: string){
    const param = new HttpParams().set('seller', seller);
    return this.http.get<ResponsePolling>(`${environment.multivendePollingUri}/validate_polling_products`, { params: param });
  }

  runPollingBySeller(seller: string){
    const param = new HttpParams().set('seller', seller);
    const headers = { 'No-Spinner': 'true' };
    return this.http.get<ResponsePolling>(`${environment.multivendePollingUri}/products_seller`, { params: param, headers: headers});
  }
}
